import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";

const PRIORITIES = [
  { v: "low", l: "Low", color: "#5C85FF" },
  { v: "normal", l: "Normal", color: "#6C50BF" },
  { v: "high", l: "High", color: "#F9BC06" },
  { v: "urgent", l: "Urgent", color: "#FF6B6B" },
];

const ALL_OPT = { value: "all", label: "Select All Employees" };

const TEMPLATES = [
  "Please complete your timesheet for this week by EOD Friday.",
  "Reminder: company-wide holiday on [date]. Office will be closed.",
  "Your payslip for [month] is now available in your portal.",
  "Please submit your self-assessment before [deadline].",
];

export default function NotificationFormNew() {
  const nav = useNavigate();
  const [empOpts, setEmpOpts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(endpoints.employees.list).then((r) => {
      const list = (Array.isArray(r.data) ? r.data : r.data?.data || []).map((e) => ({
        value: e.employeeid, label: `${e.name} (${e.employeeid})`,
      }));
      setEmpOpts([ALL_OPT, ...sortByLabel(list, (o) => o.label)]);
    }).catch(() => setEmpOpts([]));
  }, []);

  const selectedCount = recipients.some((r) => r.value === "all") ? empOpts.length - 1 : recipients.length;
  const charCount = message.length;
  const priorityColor = PRIORITIES.find((p) => p.v === priority)?.color || "#6C50BF";

  const send = async (e) => {
    e.preventDefault();
    if (selectedCount === 0) return toast.error("Pick at least one recipient");
    if (!message.trim()) return toast.error("Message is required");
    setSending(true);
    try {
      const ids = recipients.some((r) => r.value === "all")
        ? empOpts.filter((o) => o.value !== "all").map((o) => o.value)
        : recipients.map((r) => r.value);
      await api.post("/api/notification/send", {
        employeeids: ids, message, priority,
      });
      toast.success(`Notification delivered to ${ids.length} employee(s)`);
      setRecipients([]); setMessage(""); setPriority("normal");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setSending(false); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Send Notification"
        subtitle="Push an in-app alert to selected employees"
        icon={<NotificationsActiveOutlinedIcon />}
      />

      <Box component="form" onSubmit={send}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SectionCard title="Notification Details" accent="#6C50BF">
              <Stack spacing={2.5}>
                <Autocomplete
                  multiple options={empOpts} value={recipients}
                  onChange={(_, v) => {
                    if (v.some((x) => x.value === "all")) setRecipients(empOpts.filter((o) => o.value !== "all"));
                    else setRecipients(v);
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  renderTags={(value, getTagProps) =>
                    value.map((option, idx) => {
                      const { key, ...chipProps } = getTagProps({ index: idx });
                      return <Chip key={option.value} label={option.label} size="small" {...chipProps} />;
                    })
                  }
                  renderInput={(p) => <TextField {...p} label="Recipients *" placeholder="Search employees" />}
                />

                <TextField
                  fullWidth multiline minRows={6} label="Message *" value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What would you like to tell them?"
                  helperText={`${charCount} characters`}
                  required
                />

                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 1 }}>
                    Quick templates
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {TEMPLATES.map((t) => (
                      <Chip key={t} label={t.slice(0, 30) + "…"} size="small" variant="outlined" onClick={() => setMessage(t)} sx={{ cursor: "pointer" }} />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
              <SectionCard title="Summary" accent={priorityColor}>
                <Stack spacing={2}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <CampaignOutlinedIcon sx={{ color: priorityColor }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        {selectedCount > 0 ? `${selectedCount} recipient(s)` : "No recipients"}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", fontStyle: message ? "normal" : "italic", whiteSpace: "pre-wrap" }}>
                      {message || "(Message will appear here)"}
                    </Typography>
                  </Box>

                  <TextField select fullWidth size="small" label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
                    {sortByLabel(PRIORITIES, (p) => p.l).map((p) => (
                      <MenuItem key={p.v} value={p.v}>
                        <Chip size="small" label={p.l} sx={{ bgcolor: `${p.color}20`, color: p.color, fontWeight: 700, mr: 1 }} />
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendOutlinedIcon />}
                    disabled={sending || selectedCount === 0 || !message.trim()}
                  >
                    {sending ? "Sending…" : `Send${selectedCount > 0 ? ` to ${selectedCount}` : ""}`}
                  </Button>

                  <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "center" }}>
                    Recipients will see the notification in-app and as a browser push (if subscribed).
                  </Typography>
                </Stack>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
